import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  List,
  Card,
  Row,
  Col,
  Input,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Empty,
  Grid,
  Popconfirm,
  Tabs,
  Switch,
} from "antd";
import {
  ReloadOutlined,
  PhoneOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) {
    config.withCredentials = false;
  }
  return config;
});

const errorMessageFromErr = (err, fallback) => {
  return (
    err?.response?.data?.Message ||
    err?.response?.data?.message ||
    (typeof err?.response?.data === "string" ? err.response.data : "") ||
    err?.message ||
    fallback
  );
};

const openDialer = (phone) => {
  if (!phone) return;
  window.location.href = `tel:${phone}`;
};

// Tab 1: Pending catalogue-access requests, with SuperAdmin approve/reject.
// GET api/TempCatalogueAccess/GetPendingRequests, POST .../ApproveRequest, .../RejectRequest
const PendingRequestsTab = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showPricesByRequest, setShowPricesByRequest] = useState({});

  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    fetchRequests(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/TempCatalogueAccess/GetPendingRequests");
      const data = Array.isArray(res.data) ? res.data : [];
      setRequests(data);
      setVisibleCount(isMobile ? 10 : 20);
    } catch (err) {
      if (err?.response?.status === 401) {
        messageApi.error("Unauthorized. Please log in again.");
      } else {
        messageApi.error(errorMessageFromErr(err, "Failed to fetch requests."));
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (!q) return true;
      return [r.Name, r.ContactNo, r.City, r.State, r.PinCode]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q));
    });
  }, [requests, search]);

  const visibleRequests = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const loadMore = () => setVisibleCount((c) => c + (isMobile ? 10 : 20));

  const handleApprove = async (item) => {
    const id = item?.RequestID;
    setBusyId(id);
    try {
      const res = await api.post("api/TempCatalogueAccess/ApproveRequest", {
        RequestID: id,
        ShowPrices: !!showPricesByRequest[id],
      });
      messageApi.success(
        typeof res?.data === "string" ? res.data : "Approved for 24 hours."
      );
      setRequests((prev) => prev.filter((r) => r.RequestID !== id));
    } catch (err) {
      messageApi.error(errorMessageFromErr(err, "Failed to approve request."));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    const id = item?.RequestID;
    setBusyId(id);
    try {
      await api.post("api/TempCatalogueAccess/RejectRequest", null, {
        params: { RequestID: id },
      });
      messageApi.success("Request rejected.");
      setRequests((prev) => prev.filter((r) => r.RequestID !== id));
    } catch (err) {
      messageApi.error(errorMessageFromErr(err, "Failed to reject request."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {contextHolder}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#f5f6fa",
          paddingBottom: 8,
        }}
      >
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col flex="auto">
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {filtered.length} pending request{filtered.length === 1 ? "" : "s"}
            </Text>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchRequests(false)}
              loading={fetching}
              size={isMobile ? "middle" : "large"}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Card
          size="small"
          style={{ marginTop: 8, borderRadius: 10, border: "1px solid #e6f7ff" }}
          bodyStyle={{ padding: isMobile ? 8 : 12 }}
        >
          <Input
            allowClear
            placeholder="Search name, phone, city, state, pincode..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVisibleCount(isMobile ? 10 : 20);
            }}
            size={isMobile ? "middle" : "large"}
          />
        </Card>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card style={{ marginTop: 12, borderRadius: 12, border: "1px solid #f0f0f0" }}>
          <Empty description="No pending requests" />
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
          style={{ marginTop: 12 }}
          dataSource={visibleRequests}
          renderItem={(item) => {
            const id = item.RequestID;
            const dateStr = item?.RequestedOn
              ? moment(item.RequestedOn).format("DD MMM YYYY, hh:mm A")
              : "-";

            return (
              <List.Item>
                <Card
                  style={{
                    marginBottom: 12,
                    borderRadius: 12,
                    border: "1px solid #e6f7ff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                  bodyStyle={{ padding: isMobile ? 12 : 16 }}
                >
                  <Row gutter={[8, 8]} align="middle">
                    <Col xs={24}>
                      <Space direction="vertical" size={2} style={{ width: "100%" }}>
                        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                          {item?.Name || "Unknown"}
                        </Text>
                        <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
                          Requested: {dateStr}
                        </Text>
                        {(item?.IsExistingCustomer || item?.IsExistingLead) && (
                          <Space size={4} wrap style={{ marginTop: 4 }}>
                            {item?.IsExistingCustomer && (
                              <Tag color={item?.ExistingCustomerIsActive ? "green" : "blue"} style={{ margin: 0, fontSize: 10 }}>
                                Existing Customer{item?.ExistingCustomerIsActive ? " (Active)" : ""}
                              </Tag>
                            )}
                            {item?.IsExistingLead && (
                              <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>
                                {item?.LeadSourceName || "Lead"} Lead
                              </Tag>
                            )}
                          </Space>
                        )}
                      </Space>
                    </Col>

                    <Col xs={24}>
                      <Row gutter={[8, 8]}>
                        <Col xs={12}>
                          <Text type="secondary" style={{ fontSize: 12 }}>City</Text>
                          <div style={{ fontSize: 13 }}>{item?.City || "-"}</div>
                        </Col>
                        <Col xs={12}>
                          <Text type="secondary" style={{ fontSize: 12 }}>State</Text>
                          <div style={{ fontSize: 13 }}>{item?.State || "-"}</div>
                        </Col>
                        <Col xs={24}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Contact</Text>
                          <div style={{ fontSize: 13 }}>{item?.ContactNo || "-"}</div>
                        </Col>
                      </Row>
                    </Col>

                    <Col xs={24}>
                      <Space
                        align="center"
                        style={{ width: "100%", justifyContent: "space-between", marginTop: 4 }}
                      >
                        <Space size={6}>
                          <Text style={{ fontSize: 12 }}>Show prices?</Text>
                          <Switch
                            size="small"
                            checked={!!showPricesByRequest[id]}
                            onChange={(checked) =>
                              setShowPricesByRequest((prev) => ({ ...prev, [id]: checked }))
                            }
                          />
                        </Space>
                      </Space>
                    </Col>

                    <Col xs={24}>
                      <Space style={{ width: "100%", justifyContent: "flex-end" }} wrap>
                        <Button
                          icon={<PhoneOutlined />}
                          size={isMobile ? "middle" : "large"}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDialer(item?.ContactNo);
                          }}
                        >
                          Call
                        </Button>
                        <Popconfirm
                          title="Reject this request?"
                          onConfirm={() => handleReject(item)}
                          okText="Reject"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true, loading: busyId === id }}
                        >
                          <Button
                            danger
                            icon={<CloseCircleOutlined />}
                            size={isMobile ? "middle" : "large"}
                            loading={busyId === id}
                          >
                            Reject
                          </Button>
                        </Popconfirm>
                        <Popconfirm
                          title="Grant 24-hour catalogue access?"
                          description={
                            <div style={{ lineHeight: 1.6 }}>
                              <div><Text strong>Name:</Text> {item?.Name || "-"}</div>
                              <div><Text strong>Contact:</Text> {item?.ContactNo || "-"}</div>
                              <div>
                                <Text strong>Prices:</Text>{" "}
                                {showPricesByRequest[id] ? "Visible" : "Hidden"}
                              </div>
                            </div>
                          }
                          onConfirm={() => handleApprove(item)}
                          okText="Approve"
                          cancelText="Cancel"
                          okButtonProps={{
                            type: "primary",
                            icon: <CheckCircleOutlined />,
                            loading: busyId === id,
                          }}
                        >
                          <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            size={isMobile ? "middle" : "large"}
                            loading={busyId === id}
                          >
                            Approve
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
      {!loading && filtered.length > visibleRequests.length && (
        <div style={{ textAlign: "center", margin: "12px 0 24px" }}>
          <Button onClick={loadMore} size={isMobile ? "middle" : "large"}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

// Tab 2: Approved guests - extend their 24h window or convert them to a full customer.
// GET api/TempCatalogueAccess/GetActiveTempGuests, POST .../ExtendAccess, .../ConvertToFullCustomer
const ActiveGuestsTab = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchGuests(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGuests = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/TempCatalogueAccess/GetActiveTempGuests");
      const data = Array.isArray(res.data) ? res.data : [];
      setGuests(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        messageApi.error("Unauthorized. Please log in again.");
      } else {
        messageApi.error(errorMessageFromErr(err, "Failed to fetch guests."));
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guests.filter((g) => {
      if (!q) return true;
      return [g.Name, g.ContactNo].filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
    });
  }, [guests, search]);

  const expiryInfo = (guest) => {
    if (!guest?.AccessExpiresOn) {
      return { expired: true, label: "No expiry set" };
    }
    const expiresAt = moment(guest.AccessExpiresOn);
    const now = moment();
    if (!guest.IsActive || expiresAt.isSameOrBefore(now)) {
      return { expired: true, label: `Expired ${expiresAt.fromNow()}` };
    }
    const hours = expiresAt.diff(now, "hours");
    const minutes = expiresAt.diff(now, "minutes") % 60;
    return { expired: false, label: `Expires in ${hours}h ${minutes}m` };
  };

  const handleExtend = async (item) => {
    setBusyId(item.UserID);
    try {
      await api.post("api/TempCatalogueAccess/ExtendAccess", null, {
        params: { UserID: item.UserID },
      });
      messageApi.success("Access extended by 24 hours.");
      fetchGuests(false);
    } catch (err) {
      messageApi.error(errorMessageFromErr(err, "Failed to extend access."));
    } finally {
      setBusyId(null);
    }
  };

  const handleConvert = async (item) => {
    setBusyId(item.UserID);
    try {
      await api.post("api/TempCatalogueAccess/ConvertToFullCustomer", null, {
        params: { UserID: item.UserID },
      });
      messageApi.success("Converted to a full customer.");
      setGuests((prev) => prev.filter((g) => g.UserID !== item.UserID));
    } catch (err) {
      messageApi.error(errorMessageFromErr(err, "Failed to convert guest."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {contextHolder}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: "#f5f6fa", paddingBottom: 8 }}>
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col flex="auto">
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {filtered.length} guest{filtered.length === 1 ? "" : "s"}
            </Text>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchGuests(false)}
              loading={fetching}
              size={isMobile ? "middle" : "large"}
            >
              Refresh
            </Button>
          </Col>
        </Row>
        <Card size="small" style={{ marginTop: 8, borderRadius: 10, border: "1px solid #e6f7ff" }} bodyStyle={{ padding: isMobile ? 8 : 12 }}>
          <Input
            allowClear
            placeholder="Search name, phone..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size={isMobile ? "middle" : "large"}
          />
        </Card>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card style={{ marginTop: 12, borderRadius: 12, border: "1px solid #f0f0f0" }}>
          <Empty description="No temporary guests yet" />
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
          style={{ marginTop: 12 }}
          dataSource={filtered}
          renderItem={(item) => {
            const { expired, label } = expiryInfo(item);
            const busy = busyId === item.UserID;

            return (
              <List.Item>
                <Card
                  style={{ marginBottom: 12, borderRadius: 12, border: "1px solid #e6f7ff", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                  bodyStyle={{ padding: isMobile ? 12 : 16 }}
                >
                  <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                      {item?.Name || "Unknown"}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {item?.ContactNo || "-"}
                    </Text>
                    <Tag
                      icon={<ClockCircleOutlined />}
                      color={expired ? "red" : "green"}
                      style={{ margin: 0, width: "fit-content" }}
                    >
                      {label}
                    </Tag>
                    <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 8 }} wrap>
                      <Popconfirm
                        title="Extend this guest's access by 24 hours?"
                        onConfirm={() => handleExtend(item)}
                        okText="Extend"
                        cancelText="Cancel"
                        okButtonProps={{ loading: busy }}
                      >
                        <Button icon={<ClockCircleOutlined />} size={isMobile ? "middle" : "large"} loading={busy}>
                          Extend 24h
                        </Button>
                      </Popconfirm>
                      <Popconfirm
                        title="Convert to a full customer?"
                        description="Removes the 24-hour limit and enables prices + ordering."
                        onConfirm={() => handleConvert(item)}
                        okText="Convert"
                        cancelText="Cancel"
                        okButtonProps={{ type: "primary", icon: <RiseOutlined />, loading: busy }}
                      >
                        <Button type="primary" icon={<RiseOutlined />} size={isMobile ? "middle" : "large"} loading={busy}>
                          Convert
                        </Button>
                      </Popconfirm>
                    </Space>
                  </Space>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
};

const TempAccessRequests = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f6fa", padding: isMobile ? "8px" : "16px" }}>
      <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: "#1890ff", fontWeight: 700 }}>
        Temporary Catalogue Access
      </Title>
      <Tabs
        defaultActiveKey="pending"
        style={{ marginTop: 8 }}
        items={[
          { key: "pending", label: "Pending Requests", children: <PendingRequestsTab /> },
          { key: "active", label: "Active / Expired Guests", children: <ActiveGuestsTab /> },
        ]}
      />
    </div>
  );
};

export default TempAccessRequests;
